import { useMemo } from "react";

import { Button } from "@/components/ui/button";

import { PdfFileCard } from "../components/PdfFileCard";
import { UploadProgressStrip } from "../components/UploadProgressStrip";
import { getGroupsReadyForUpload } from "../state/selectors";
import type {
  CustomerMinimal,
  FileEntry,
  Group,
  WizardData,
} from "../state/types";
import type { UploadStatus } from "../upload/types";

type PhaseUploadingProps = {
  data: WizardData;
  customers: CustomerMinimal[];
  /**
   * Per-group upload status. A missing key is treated as
   * `{ kind: "idle" }` (groups not yet picked up by the concurrency
   * pool — they show as "Pending").
   */
  statusByGroupId: Map<string, UploadStatus>;
  /**
   * Operator clicks Done — disabled while any group is still
   * non-terminal so the user can't navigate away mid-upload.
   */
  onDone: () => void;
};

function isTerminal(status: UploadStatus): boolean {
  return status.kind === "complete" || status.kind === "failed";
}

function statusFor(
  statusByGroupId: Map<string, UploadStatus>,
  groupId: string,
): UploadStatus {
  const existing = statusByGroupId.get(groupId);
  return existing ?? { kind: "idle" };
}

/**
 * Phase 5 — Uploading.
 *
 * One card per group-ready-for-upload, each showing customer name,
 * filenames, and the `UploadProgressStrip` driven by `statusByGroupId`.
 *
 * If any group has failed by the time we reach a fully terminal state,
 * a red summary banner lists the affected customers — failures are
 * terminal for the session (no retry button per spec).
 */
export function PhaseUploading({
  data,
  customers,
  statusByGroupId,
  onDone,
}: PhaseUploadingProps) {
  const groupsToUpload = useMemo(
    (): Group[] => getGroupsReadyForUpload(data),
    [data],
  );

  const customerById = useMemo((): Map<string, CustomerMinimal> => {
    const map = new Map<string, CustomerMinimal>();
    for (const c of customers) map.set(c.id, c);
    return map;
  }, [customers]);

  const allTerminal = useMemo((): boolean => {
    if (groupsToUpload.length === 0) return true;
    for (const g of groupsToUpload) {
      if (!isTerminal(statusFor(statusByGroupId, g.id))) return false;
    }
    return true;
  }, [groupsToUpload, statusByGroupId]);

  const failedGroups = useMemo((): Group[] => {
    if (!allTerminal) return [];
    return groupsToUpload.filter(
      (g: Group) => statusFor(statusByGroupId, g.id).kind === "failed",
    );
  }, [allTerminal, groupsToUpload, statusByGroupId]);

  const completedCount = useMemo((): number => {
    let count = 0;
    for (const g of groupsToUpload) {
      if (statusFor(statusByGroupId, g.id).kind === "complete") count += 1;
    }
    return count;
  }, [groupsToUpload, statusByGroupId]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <h2 className="text-lg font-semibold">
          {allTerminal ? "Upload complete" : "Uploading batches"}
        </h2>
        <p className="text-sm text-muted-foreground">
          {allTerminal
            ? `${completedCount} of ${groupsToUpload.length} batch${
                groupsToUpload.length === 1 ? "" : "es"
              } completed.`
            : "One backend batch is being created per customer group. Up to 2 groups upload in parallel; PDFs inside a group upload sequentially."}
        </p>
      </div>

      {failedGroups.length > 0 ? (
        <div className="rounded border border-destructive bg-destructive/10 p-3 text-xs text-destructive flex flex-col gap-1">
          <span className="font-semibold uppercase tracking-wide text-[10px]">
            {failedGroups.length} batch
            {failedGroups.length === 1 ? "" : "es"} failed and {failedGroups.length === 1 ? "was" : "were"} abandoned
          </span>
          <span>
            Per-group failure details appear below. To retry, restart the
            wizard from the beginning — failed batches are not resumable
            within this session.
          </span>
          <ul className="flex flex-col gap-0.5 mt-1">
            {failedGroups.map((g: Group) => {
              const customer =
                g.selected_customer_id !== null
                  ? customerById.get(g.selected_customer_id)
                  : undefined;
              const customerLabel = customer
                ? customer.long_name
                : "(unknown customer)";
              return (
                <li key={g.id} className="text-[11px]">
                  •{" "}
                  <span className="font-semibold">{customerLabel}</span>{" "}
                  — {g.file_ids.length} PDF
                  {g.file_ids.length === 1 ? "" : "s"}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {groupsToUpload.length === 0 ? (
        <div className="rounded border border-dashed border-muted-foreground/40 bg-muted/20 p-6 text-sm text-muted-foreground">
          No groups to upload.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {groupsToUpload.map((group: Group) => {
            const status = statusFor(statusByGroupId, group.id);
            const customer =
              group.selected_customer_id !== null
                ? customerById.get(group.selected_customer_id)
                : undefined;
            const customerLabel = customer
              ? customer.long_name
              : "(unknown customer)";
            return (
              <div
                key={group.id}
                className="rounded-md border bg-card p-3 flex flex-col gap-2"
              >
                <div className="flex flex-col gap-0.5">
                  <span className="text-[10px] uppercase font-semibold tracking-wide text-muted-foreground">
                    Customer
                  </span>
                  <span
                    className="text-sm font-semibold break-words"
                    title={customerLabel}
                  >
                    {customerLabel}
                  </span>
                  <span className="text-[10px] text-muted-foreground">
                    {group.file_ids.length} PDF
                    {group.file_ids.length === 1 ? "" : "s"}
                  </span>
                </div>

                <UploadProgressStrip status={status} />

                <details className="text-xs">
                  <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                    Files
                  </summary>
                  <ul className="flex flex-col gap-1 mt-1">
                    {group.file_ids.map((fileId: string) => {
                      const entry: FileEntry | undefined = data.files[fileId];
                      if (!entry) return null;
                      return (
                        <li key={fileId}>
                          <PdfFileCard
                            filename={entry.file.name}
                            density="compact"
                          />
                        </li>
                      );
                    })}
                  </ul>
                </details>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex flex-row justify-end">
        <Button onClick={onDone} disabled={!allTerminal}>
          {allTerminal ? "Done — back to list" : "Uploading…"}
        </Button>
      </div>
    </div>
  );
}
